/**
 * Cross-browser smooth scroll utility.
 * Uses requestAnimationFrame for consistent timing across Chrome & Safari,
 * since native `behavior: "smooth"` varies significantly between browsers.
 */

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export function smoothScrollTo(
  targetY: number,
  duration = 800
): void {
  const start = window.scrollY;
  const distance = targetY - start;
  if (distance === 0) return;

  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, start + distance * easeInOutCubic(progress));
    if (progress < 1) requestAnimationFrame(animate);
  };

  requestAnimationFrame(animate);
}

export function smoothScrollToTop(duration = 800): void {
  smoothScrollTo(0, duration);
}

export function smoothScrollToElement(
  el: Element,
  duration = 800,
  block: "start" | "center" = "center"
): void {
  const rect = el.getBoundingClientRect();
  const scrollY = window.scrollY;
  let targetY: number;

  if (block === "center") {
    targetY = scrollY + rect.top - window.innerHeight / 2 + rect.height / 2;
  } else {
    targetY = scrollY + rect.top;
  }

  targetY = Math.max(0, targetY);
  smoothScrollTo(targetY, duration);
}
